import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Search, ArrowRight } from "lucide-react";
import { TOOLS, CATEGORIES } from "@/lib/tools";

const Tile = ({ tool, index }) => {
  const Icon = tool.icon;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.03 }}>
      <Link to={tool.path} data-testid={`tile-${tool.id}`}
        className="group block h-full rounded-2xl bg-white border border-[#E8E3D9] p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
        <div className="flex items-start justify-between">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${tool.accent}18` }}>
            <Icon className="h-5.5 w-5.5" style={{ color: tool.accent }} strokeWidth={2} />
          </div>
          {tool.flagship && <span className="text-[10px] uppercase tracking-wide font-bold text-[#D99B26] bg-[#FBEDD9] px-2 py-0.5 rounded-full">Flagship</span>}
          {tool.ai && !tool.flagship && <span className="text-[10px] uppercase tracking-wide font-bold text-[#2A6F7F] bg-[#EAF0F2] px-2 py-0.5 rounded-full">AI</span>}
        </div>
        <h3 className="font-heading text-lg font-bold text-[#1A231F] mt-4">{tool.title}</h3>
        <p className="text-sm text-[#5C6A64] mt-1 leading-snug">{tool.tagline}</p>
        <div className="mt-4 flex items-center gap-1.5 text-sm font-semibold" style={{ color: tool.accent }}>
          Open <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>
    </motion.div>
  );
};

export default function Home() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return TOOLS;
    return TOOLS.filter((x) => (x.title + " " + x.tagline + " " + x.category).toLowerCase().includes(t));
  }, [q]);

  return (
    <div className="min-h-screen aura-grain">
      <header className="sticky top-0 z-40 border-b border-[#E8E3D9] bg-[#FBF9F5]/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-10 py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#1E2B25] flex items-center justify-center shadow-sm">
              <Sparkles className="h-5 w-5 text-[#D99B26]" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <h1 className="font-heading text-xl font-extrabold tracking-tight text-[#1A231F]">My Finanz Planner</h1>
              <p className="text-[11px] text-[#8A9790] font-medium leading-snug max-w-[280px]">Personal Finance Tools &amp; Calculators to plan, grow and streamline your personal finance journey</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#E8E3D9]">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/afc10d1d-fd46-4f4c-932c-d61736f98c3e/images/849f84a5ccf6a9f1c02fc8a947fd2a82ecf3d6b747b2fef6ea32f6d26af3c09e.jpeg"
            alt="Mountain sunset" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E2B25]/94 via-[#1E2B25]/72 to-[#1E2B25]/35" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-[#D99B26] mb-3">Your money, all in one place</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Every calculator you need to <span className="text-[#D99B26]">plan with confidence</span>.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-white/75 leading-relaxed">
              Invest, borrow, protect and plan — a growing suite of India-first tools with beautiful visuals,
              AI guidance and exportable reports.
            </p>
            <div className="mt-6 relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8A9790]" />
              <input data-testid="tool-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search tools — SIP, home loan, tax…"
                className="w-full h-12 pl-11 pr-4 rounded-full bg-white/95 border border-white/20 text-[#1A231F] placeholder:text-[#8A9790] font-medium focus:outline-none focus:ring-2 focus:ring-[#D99B26]" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Tiles by category */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 space-y-12">
        {CATEGORIES.map((cat) => {
          const items = filtered.filter((t) => t.category === cat);
          if (!items.length) return null;
          return (
            <section key={cat} data-testid={`category-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-3 mb-5">
                <h3 className="font-heading text-xl font-bold text-[#1A231F]">{cat}</h3>
                <span className="text-xs font-semibold text-[#8A9790] bg-[#F0ECE3] px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {items.map((t, i) => <Tile key={t.id} tool={t} index={i} />)}
              </div>
            </section>
          );
        })}
        {!filtered.length && <p className="text-center text-[#8A9790] py-12">No tools match "{q}".</p>}
      </div>

      <footer className="border-t border-[#E8E3D9] py-6 text-center">
        <p className="text-xs text-[#8A9790]">My Finanz Planner · Estimates are illustrative and not financial advice.</p>
      </footer>
    </div>
  );
}
